import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { debounceTime, switchMap} from 'rxjs/operators';
import { LoadingController, ModalController } from '@ionic/angular';
import { BackendCallService } from 'src/app/services/backend-call/backend-call.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { Application, ControlSystem, ControlSytemData, MachineAttribute, SpecificFeature } from 'src/app/pages/project-management/machine-details/machine-details-interface';
import { AppConstants } from 'src/app/utilities/constant';
import { AlertModalPage } from 'src/app/components/alert-modal/alert-modal.page';


@Component({
  selector: 'app-machine-attribute-details',
  templateUrl: './machine-attribute-details.component.html',
  styleUrls: ['./machine-attribute-details.component.scss'],
})
export class MachineAttributeDetailsComponent implements OnInit, OnChanges, AfterViewChecked {
  @ViewChild('machineForm', { static: false }) machineForm: any;
  @Input() global_Id: string;
  @Input() machineId: string;  
  @Input() isCloneMode = false;
  public attributesData: any;
  public formData: any;
  public isSaveDisabled = true;
  public selectedMachineType = '';
  public selectedApplication = '';
  private originalFormData: any;
  private onApplicationScrolled = new EventEmitter();
  private onMachineTypeScrolled = new EventEmitter();
  private onFeatureScrolled = new EventEmitter();
  private onControlSystemScrolled = new EventEmitter();
  private count = 20;
  // Following count is used to update the value of increasedCount onScroll of respective dropdown
  private increasedApplicationCount = 0;
  private increasedMachineTypeCount = 0;
  private increasedFeaturesCount = 0;
  private increasedControlSystemCount = 0;
  public applicationLimitReached = false;
  public machineTypeLimitReached = false;
  public featureLimitReached = false;
  public controlSystemLimitReached = false;
  // Following lists will be used to show the data in the form under respective dropdown
  public applicationList = [];
  public machineTypeList = [];
  public featureList = [];
  public controlSystemList = [];
  public isEditAttributes = false;
  public selectedControlSystem: string = '-';
  public selectedFeatures: string = '-';

  constructor(
    public backendService: BackendCallService,
    private modalController: ModalController,
    public loading: LoadingController,
    private toastService: ToastService,
    public translate: TranslateService,
    private elementRef: ElementRef<HTMLElement>,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.isCloneMode && !changes.isCloneMode.firstChange)
      || (changes.machineId && !changes.machineId.firstChange && changes.machineId.previousValue !== this.machineId)) {
      this.isEditAttributes = this.isCloneMode;
      this.attributesData = null;
      this.formData = null;
      this.getMachineAttributes();
    }
  }

  ngOnInit() {
    if (this.isCloneMode) {
      this.isEditAttributes = true;
    }
    this.getMachineAttributes();
    this.subscribeApplicationScroll();
    this.subscribeMachineTypeScroll();
    this.subscribeFeaturesScroll();
    this.subscribeControlSystemScroll();
    this.onApplicationScrolled.emit();
    this.onMachineTypeScrolled.emit();
    this.onFeatureScrolled.emit();
    this.onControlSystemScrolled.emit();
    this.initializeForm();
  }

  ngAfterViewChecked(): void {
    if (this.isEditAttributes) {
      this.ensureNgSelectKeyboardAccessibility();
    }
  }

  private ensureNgSelectKeyboardAccessibility(): void {
    const host = this.elementRef?.nativeElement;
    if (!host) {
      return;
    }

    const allSelects = host.querySelectorAll<HTMLElement>('ng-select[data-field]');
    allSelects.forEach(select => {
      const clearWrapper = select.querySelector<HTMLElement>('.ng-clear-wrapper');
      if (clearWrapper) {
        clearWrapper.tabIndex = 0;
        clearWrapper.setAttribute('role', 'button');
        clearWrapper.setAttribute('aria-label', this.translate.instant('Clear selection'));

        const clearIcon = clearWrapper.querySelector<HTMLElement>('.ng-clear');
        if (clearIcon) {
          clearIcon.tabIndex = -1;
          clearIcon.removeAttribute('role');
          clearIcon.removeAttribute('aria-label');
        }

        if (clearWrapper.getAttribute('data-keyboard-wired') !== 'true') {
          clearWrapper.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key !== 'Enter' && event.key !== ' ') {
              return;
            }

            event.preventDefault();
            event.stopPropagation();
            const field = select.getAttribute('data-field');
            if (field) {
              this.clearFieldValue(field);
            }
            const container = clearWrapper.closest('.ng-select-container') as HTMLElement;
            container?.focus();
          }, true);

          clearWrapper.setAttribute('data-keyboard-wired', 'true');
        }
      }

      const valueIcons = select.querySelectorAll<HTMLElement>('.ng-value-icon');
      valueIcons.forEach(icon => {
        icon.tabIndex = 0;
        icon.setAttribute('role', 'button');
        icon.setAttribute('aria-label', this.translate.instant('Remove selected value'));

        if (icon.getAttribute('data-keyboard-wired') === 'true') {
          return;
        }

        icon.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.key !== 'Enter' && event.key !== ' ') {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          icon.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
          icon.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
          icon.click();
          this.onFormChange();
        }, true);

        icon.setAttribute('data-keyboard-wired', 'true');
      });
    });
  }

  private clearFieldValue(field: string): void {
    if (!this.formData) {
      return;
    }

    if (field === 'machineType' || field === 'application') {
      this.formData[field] = null;
      this.onFormChange();
      return;
    }

    if (field === 'controlSystem' || field === 'features') {
      this.formData[field] = [];
      this.onFormChange();
    }
  }

  /**
   * This method is called when the user clicks on the edit button.
   */
  onEdit() {
    this.isEditAttributes = true;
  }

  /**
   * This method initializes the form data with machine attributes data.
   */
  private initializeForm() {
    this.ensureAttributeDefaults();
    this.formData = {
      manufacturer: this.attributesData?.manufacturer || this.translate.instant('not_available'),
      machine_Model: this.isCloneMode ? '' : this.attributesData?.machine_Model || this.translate.instant('not_available'),
      serial_Number: this.isCloneMode ? '' : this.attributesData?.serial_Number || this.translate.instant('not_available'),
      asset_Number: this.isCloneMode ? '' : this.attributesData?.asset_Number || this.translate.instant('not_available'),
      application: this.getEntityId(this.attributesData?.machineMetaData?.application),
      machineType: this.getEntityId(this.attributesData?.machineMetaData?.machineType),
      features: this.getEntityIds(this.attributesData?.machineMetaData?.specificFeature),
      controlSystem: this.getEntityIds(this.attributesData?.machineMetaData?.controlSystem),
    };
    this.originalFormData = JSON.parse(JSON.stringify({
      attributesData: this.attributesData,
      formData: this.formData
    }));
    this.isSaveDisabled = true;
    const features = this.attributesData?.machineMetaData?.specificFeature || [];
    this.selectedFeatures = features.length ? features.map(f => f.specificFeature).filter(Boolean).join('  ,  ') : null;
    const controlSystem = this.attributesData?.machineMetaData?.controlSystem || [];
    this.selectedControlSystem = controlSystem.length ? controlSystem.map(cs => cs.controlSystem).filter(Boolean).join('  ,  ') : null;
    this.featureList = this.includeSelectedOptions(this.featureList, features);
    this.controlSystemList = this.includeSelectedOptions(this.controlSystemList, controlSystem);
    this.machineTypeList = this.includeSelectedOptions(this.machineTypeList, [this.attributesData?.machineMetaData?.machineType]);
    this.applicationList = this.includeSelectedOptions(this.applicationList, [this.attributesData?.machineMetaData?.application]);
    this.syncSelectedAttributeDisplay();
  }

  private includeSelectedOptions(options: any[], selectedOptions: any[]): any[] {
    const selected = (selectedOptions || []).filter(option => option && this.getEntityId(option));
    const missingOptions = selected.filter(option =>
      !options.some(existing => String(this.getEntityId(existing)) === String(this.getEntityId(option)))
    );
    return [...missingOptions, ...options];
  }

  private appendUniqueOptions(options: any[], newOptions: any[]): any[] {
    const optionIds = new Set(options.map(option => String(this.getEntityId(option))));
    const uniqueNewOptions = newOptions.filter(option => {
      const id = String(this.getEntityId(option));
      if (optionIds.has(id)) {
        return false;
      }

      optionIds.add(id);
      return true;
    });
    return [...options, ...uniqueNewOptions];
  }

  private ensureAttributeDefaults(): void {
    const machineMetaData = this.attributesData?.machineMetaData || {};
    this.attributesData = {
      ...(this.attributesData || {}),
      machineMetaData: {
        ...machineMetaData,
        application: machineMetaData.application || null,
        machineType: machineMetaData.machineType || null,
        specificFeature: Array.isArray(machineMetaData.specificFeature) ? machineMetaData.specificFeature : [],
        controlSystem: Array.isArray(machineMetaData.controlSystem) ? machineMetaData.controlSystem : [],
      },
    };
  }

  public getCloneAttributeDetailsPayload(): any {
    if (!this.attributesData || !this.formData) {
      return null;
    }

    return {
      manufacturer: this.getCloneOptionalText(this.formData?.manufacturer),
      machine_Model: this.getCloneOptionalText(this.formData?.machine_Model),
      serial_Number: this.getCloneOptionalText(this.formData?.serial_Number),
      asset_Number: this.getCloneOptionalText(this.formData?.asset_Number),
      application: this.formData?.application || '',
      machineType: this.formData?.machineType || '',
      specificFeature: this.formData?.features || [],
      controlSystem: this.formData?.controlSystem || [],
    };
  }

  private getCloneOptionalText(value: any): string {
    const normalizedValue = (value || '').toString().trim();
    const translatedNotAvailable = (this.translate.instant('not_available') || '').trim().toLowerCase();
    const lowerValue = normalizedValue.toLowerCase();

    if (!normalizedValue || lowerValue === 'not available' || lowerValue === 'not_available' || lowerValue === translatedNotAvailable) {
      return '';
    }

    return normalizedValue;
  }

  private serializeOptionalTextField(value?: string | null): string {
    const normalizedValue = (value || '').trim();
    const translatedNotAvailable = (this.translate.instant('not_available') || '').trim().toLowerCase();
    const lowerValue = normalizedValue.toLowerCase();

    if (!normalizedValue || lowerValue === 'not available' || lowerValue === 'not_available' || lowerValue === translatedNotAvailable) {
      return '';
    }

    return value ?? '';
  }

  onOptionalTextFieldChange(
    field: 'manufacturer' | 'machine_Model' | 'serial_Number' | 'asset_Number',
    value: string | null
  ): void {
    this.formData[field] = value ?? '';
    this.onFormChange();
  }

  private getEntityId(value: any): string {
    if (!value) {
      return '';
    }

    return value?.id || value?.applicationId || value?.machineTypeId || value;
  }

  private getEntityIds(values: any): string[] {
    if (!Array.isArray(values)) {
      return [];
    }

    return values.map((value) => this.getEntityId(value)).filter(Boolean);
  }

  /**
   * This method is called when the user scrolls down the application dropdown.
   */
  fetchMoreApplication() {
    this.onApplicationScrolled.emit();
  }

  /**
   * This method is called when the user scrolls down the machine type dropdown.
   */
  fetchMoreMachineType() {
    this.onMachineTypeScrolled.emit();
  }

  /**
   * This method is called when the user scrolls down the control system dropdown.
   */
  fetchMoreControlSystem() {
    this.onControlSystemScrolled.emit();
  }

  /**
   * This method is called when the user scrolls down the specific features dropdown.
   */
  fetchMoreFeature() {
    this.onFeatureScrolled.emit();
  }

  /**
   * This method is called to display the data under machine type dropdown.
   */
  public subscribeMachineTypeScroll() {
    this.onMachineTypeScrolled.pipe(debounceTime(100), switchMap((res) => {
      const payload = {
        search: ' ',
        count: this.count,
        increasedCount: this.increasedMachineTypeCount,
      };
      return this.backendService.getMachineTypeData(payload);
    })).subscribe(
      (data: any) => {
        data.value = data?.value
          ?.map((ele: any) => ({
            id: ele.id,
            machine_type: ele.machine_type,
            machineType: ele.machine_type,
          }))
          .filter((ele: any) => ele.machine_type !== 'NA'); // <-- exclude NA
        if (data?.value && data?.value?.length) {
          this.increasedMachineTypeCount = this.increasedMachineTypeCount + this.count;
          if (this.machineTypeList.length > 1) {
            if (data.value.length !== 0) {
              this.machineTypeList = this.appendUniqueOptions(this.machineTypeList, data.value);
            } else {
              this.increasedMachineTypeCount = 0;
              this.machineTypeLimitReached = data.value.length !== this.count;
              this.toastService.presentToast(this.translate.instant('no_results_found'), 4000, AppConstants.ERROR_SNACKBAR, 'top');
            }
          } else {
            this.increasedMachineTypeCount = 0;
            if (data.value.length !== 0) {
              this.machineTypeList = this.appendUniqueOptions(this.machineTypeList, data.value);
            } else {
              this.increasedMachineTypeCount = 0;
              this.machineTypeLimitReached = data.value.length !== this.count;
              this.toastService.presentToast(this.translate.instant('no_results_found'), 4000, AppConstants.ERROR_SNACKBAR, 'top');
            }
          }
          this.increasedMachineTypeCount = this.increasedMachineTypeCount + this.count;
        }
        setTimeout(() => {}, 500);
      },
      err => {}
    );
  }

  /**
   * This method is called to display the data under application dropdown.
   */
  public subscribeApplicationScroll() {
    this.onApplicationScrolled.pipe(debounceTime(100), switchMap((res) => {
      const payload = {
        search: ' ',
        count: this.count,
        increasedCount: this.increasedApplicationCount,
      };
      return this.backendService.getApplicationData(payload);
    })).subscribe(
      (data: { value: Application[] }) => {
        if (data?.value && data?.value?.length) {
          const filteredApps = data.value.filter(app => app.application !== 'NA');
          this.increasedApplicationCount = this.increasedApplicationCount + this.count;
          if (this.applicationList.length > 1) {
            if (filteredApps.length !== 0) {
              this.applicationList = this.appendUniqueOptions(this.applicationList, filteredApps);
            } else {
              this.increasedApplicationCount = 0;
              this.applicationLimitReached = filteredApps.length !== this.count;
              this.toastService.presentToast(
                this.translate.instant('no_results_found'), 4000, AppConstants.ERROR_SNACKBAR, 'top');
            }
          } else {
            this.increasedApplicationCount = 0;
            if (filteredApps.length !== 0) {
              this.applicationList = this.appendUniqueOptions(this.applicationList, filteredApps);
            } else {
              this.increasedApplicationCount = 0;
              this.applicationLimitReached = filteredApps.length !== this.count;
              this.toastService.presentToast(this.translate.instant('no_results_found'), 4000, AppConstants.ERROR_SNACKBAR, 'top');
            }
          }
          this.increasedApplicationCount = this.increasedApplicationCount + this.count;
        }
        setTimeout(() => {}, 500);
      },
      err => {}
    );
  }

  /**
   * This method is called to display the data under control system dropdown.
   */
  public subscribeControlSystemScroll() {
    this.onControlSystemScrolled.pipe(debounceTime(100), switchMap((res) => {
      const payload =
      {
        search: ' ',
        count: this.count,
        increasedCount: this.increasedControlSystemCount,
      };
      return this.backendService.getControlSystemData(payload);
    })).subscribe(
      (data: ControlSytemData) => {
        data.value = data?.value
        ?.filter((ele: ControlSystem) => ele.control_system !== 'NA')
        ?.map((ele: ControlSystem) => ({
          id: ele.id,
          control_system: ele.control_system,
          controlSystem: ele.control_system,
        }));
        if (data?.value && data?.value?.length) {
          this.increasedControlSystemCount = this.increasedControlSystemCount + this.count;
          if (this.controlSystemList.length > 1) {
            if (data.value.length !== 0) {
              this.controlSystemList = this.appendUniqueOptions(this.controlSystemList, data.value);
            } else {
              this.increasedControlSystemCount = 0;
              this.controlSystemLimitReached = data.value.length !== this.count;              
              this.toastService.presentToast(this.translate.instant('no_results_found'), 4000, AppConstants.ERROR_SNACKBAR, 'top');
            }
          } else {
            this.increasedControlSystemCount = 0;
            if (data.value.length !== 0) {
              this.controlSystemList = this.appendUniqueOptions(this.controlSystemList, data.value);
            } else {
              this.increasedControlSystemCount = 0;
              this.controlSystemLimitReached = data.value.length !== this.count;
              this.toastService.presentToast(this.translate.instant('no_results_found'), 4000, AppConstants.ERROR_SNACKBAR, 'top');
            }
          }
          this.increasedControlSystemCount = this.increasedControlSystemCount + this.count;
        }
        setTimeout(() => {
        }, 500);
      }, err => {
      }
    );
  }
  
  /**
   * This method is called to display the data under specific features dropdown.
   */
  public subscribeFeaturesScroll() {
    this.onFeatureScrolled.pipe(debounceTime(100), switchMap((res) => {
      const payload =
      {
        search: ' ',
        count: this.count,
        increasedCount: this.increasedFeaturesCount,
      };
      return this.backendService.getFeaturesData(payload);
    })).subscribe(
      (data: { value: SpecificFeature[] }) => {
        data.value = data?.value
        ?.filter((ele: SpecificFeature) => ele.specific_feature !== 'NA')
        ?.map((ele: SpecificFeature) => ({
          id: ele.id,
          specific_feature: ele.specific_feature,
          specificFeature: ele.specific_feature,
        }));
        if (data?.value && data?.value?.length) {
          this.increasedFeaturesCount = this.increasedFeaturesCount + this.count;
          if (this.featureList.length > 1) {
            if (data.value.length !== 0) {
              this.featureList = this.appendUniqueOptions(this.featureList, data.value);
            } else {
              this.increasedFeaturesCount = 0;
              this.featureLimitReached = data.value.length !== this.count;              
              this.toastService.presentToast(this.translate.instant('no_results_found'), 4000, AppConstants.ERROR_SNACKBAR, 'top');
            }
          } else {
            this.increasedFeaturesCount = 0;
            if (data.value.length !== 0) {
              this.featureList = this.appendUniqueOptions(this.featureList, data.value);
            } else {
              this.increasedFeaturesCount = 0;
              this.featureLimitReached = data.value.length !== this.count;
              this.toastService.presentToast(this.translate.instant('no_results_found'), 4000, AppConstants.ERROR_SNACKBAR, 'top');
            }
          }
          this.increasedFeaturesCount = this.increasedFeaturesCount + this.count;
        }
      }, err => {
      }
    );
  }

  /**
   * This method is called when the form data changes.
   */
  onFormChange(): void {
    this.syncSelectedAttributeDisplay();
    const current = JSON.stringify({
      attributesData: this.attributesData,
      formData: this.formData
    });
    const original = JSON.stringify(this.originalFormData);
    const isFormUnchanged = current === original;
    const isFormInvalid = this.machineForm?.invalid;
    this.isSaveDisabled = isFormUnchanged || isFormInvalid;
  }

  private syncSelectedAttributeDisplay(): void {
    if (!this.formData || !this.attributesData) {
      return;
    }

    const currentMachineMetaData = this.attributesData.machineMetaData || {};
    const selectedMachineMetaData = this.getSelectedMachineMetaData({
      application: this.formData.application || '',
      machineType: this.formData.machineType || '',
      specificFeature: this.formData.features || [],
      controlSystem: this.formData.controlSystem || []
    }, currentMachineMetaData);

    this.attributesData = {
      ...this.attributesData,
      machineMetaData: {
        ...currentMachineMetaData,
        ...selectedMachineMetaData
      }
    };

    this.selectedMachineType = selectedMachineMetaData.machineType?.machineType
      || selectedMachineMetaData.machineType?.machine_type
      || '';
    this.selectedApplication = selectedMachineMetaData.application?.application || '';
    this.selectedFeatures = selectedMachineMetaData.specificFeature
      .map(feature => feature?.specificFeature || feature?.specific_feature)
      .filter(Boolean)
      .join('  ,  ') || null;
    this.selectedControlSystem = selectedMachineMetaData.controlSystem
      .map(system => system?.controlSystem || system?.control_system)
      .filter(Boolean)
      .join('  ,  ') || null;
  }

  /**
   * This method is called when the user clicks on the cancel button.
   */
  async onCancel() {
    let msg: string;
    this.translate.get(AppConstants.ARE_YOU_SURE_TO_CANCEL).subscribe(res => {
      msg = res;
    });
    const props = { alertContent: msg , showCancelButton: true};
    const modal = await this.modalController.create({
      component: AlertModalPage,
      cssClass: 'small-alert-modal',
      backdropDismiss: false,
      showBackdrop: false,
      componentProps: props,
    });
    await modal.present();
    const { data: { isConfirmed } } = await modal.onWillDismiss();
    if (isConfirmed) {
      this.isEditAttributes = false;
      this.getMachineAttributes();
    }
  }

  /**
   * This method fetches the machine attributes data from the backend service.
   */
  getMachineAttributes(machineMetaDataOverride?: any) {
    this.backendService.getMachinesAttributesData(this.machineId).subscribe((attributeDataResult: MachineAttribute) => {
      const fetchedAttributes: any = attributeDataResult?.value || {};
      this.attributesData = machineMetaDataOverride
        ? {
          ...fetchedAttributes,
          machineMetaData: {
            ...(fetchedAttributes.machineMetaData || {}),
            ...machineMetaDataOverride
          }
        }
        : fetchedAttributes;
      this.initializeForm();
    });
  }

  /**
   * This method is called when the user clicks on the save button.
   */
  onSave() {
    if (this.isCloneMode) {
      return;
    }
    const editedTextFields = {
      serial_Number: this.serializeOptionalTextField(this.formData?.serial_Number),
      asset_Number: this.serializeOptionalTextField(this.formData?.asset_Number),
      manufacturer: this.serializeOptionalTextField(this.formData?.manufacturer),
      machine_Model: this.serializeOptionalTextField(this.formData?.machine_Model)
    };
    const selectedAttributeIds = {
      application: this.formData?.application || '',
      machineType: this.formData?.machineType || '',
      specificFeature: this.formData?.features || [],
      controlSystem: this.formData?.controlSystem || []
    };
    const currentMachineMetaData = this.attributesData?.machineMetaData || {};
    let updatedAttributes: any;
    // Refetch so Machine Details fields saved from the other tab aren't overwritten with stale values.
    this.backendService.getMachinesAttributesData(this.machineId).pipe(
      switchMap((latestResult: MachineAttribute) => {
        const latest: any = latestResult?.value || this.attributesData || {};
        updatedAttributes = {
          ...latest,
          ...editedTextFields,
          machineMetaData: {
            ...(latest.machineMetaData || {}),
            ...this.getSelectedMachineMetaData(selectedAttributeIds, currentMachineMetaData)
          }
        };
        const payload = {
          id: this.machineId,
          machine_Description: this.serializeOptionalTextField(latest?.machine_Description ?? latest?.description),
          machineName: latest?.machine_name,
          customerId: latest?.customer_Id || '',
          customer_Name: latest?.customer_Name,
          date_of_placing_of_market: latest?.date_of_placing_of_market || null,
          industry_type: latest?.industry_type || '',
          machine_location: this.serializeOptionalTextField(latest?.machine_location),
          date_of_inspection: latest?.date_of_inspection || null,
          certificate: this.serializeOptionalTextField(latest?.certificate),
          machine_Status: latest?.machine_Status || '',
          machineModes: latest?.machineModes,
          description: this.serializeOptionalTextField(latest?.description),
          ...editedTextFields,
          application: selectedAttributeIds.application,
          machineType: selectedAttributeIds.machineType,
          specificFeature: selectedAttributeIds.specificFeature,
          controlSystem: selectedAttributeIds.controlSystem,
          machineFilesList: [],
        };
        return this.backendService.editMachineLevelAttributes(payload);
      })
    ).subscribe((data: any) => {
      if (data.statusCode === 200) {
        this.toastService.presentToast(
          this.translate.instant('customer_management_updated_successfully', { entity: this.translate.instant('Attributes Details') }),
          4000,
          AppConstants.SUCCESS_SNACKBAR,
          'top'
        );
        this.attributesData = updatedAttributes;
        this.initializeForm();
      } else {
        this.toastService.presentToast(data.message, 4000, AppConstants.ERROR_SNACKBAR, 'top', 'error_occurred');
      }
      this.isEditAttributes = false;
    }, err => {
      this.getMachineAttributes();
      this.isEditAttributes = false;
    });
  }

  private getSelectedMachineMetaData(selectedIds: any, currentMachineMetaData: any): any {
    const findFallback = (options: any[], id: string): any =>
      (options || []).find(option => String(this.getEntityId(option)) === String(id));

    return {
      application: this.getSelectedOption(this.applicationList, selectedIds.application, currentMachineMetaData.application),
      machineType: this.getSelectedOption(this.machineTypeList, selectedIds.machineType, currentMachineMetaData.machineType),
      specificFeature: selectedIds.specificFeature.map((id: string) =>
        this.getSelectedOption(this.featureList, id, findFallback(currentMachineMetaData.specificFeature, id))
      ),
      controlSystem: selectedIds.controlSystem.map((id: string) =>
        this.getSelectedOption(this.controlSystemList, id, findFallback(currentMachineMetaData.controlSystem, id))
      )
    };
  }

  private getSelectedOption(options: any[], id: any, fallback?: any): any {
    if (!id) {
      return null;
    }

    return options.find(option => String(this.getEntityId(option)) === String(id))
      || (fallback && String(this.getEntityId(fallback)) === String(id) ? fallback : { id });
  }

}